/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'jquery',
	'../lib/@waca/core-client/js/core-client/ui/AccessibleView',
	'../lib/@waca/core-client/js/core-client/utils/Utils',
	'@ba/ba-ui-carbon-toolkit',
	'react-dom',
	'react'
], function($, BaseClass, Utils, Toolkit, ReactDOM, React) {
	/**
	 * Shows flyouts on click/tap on DOM elements identified using a selector.
	 * Content of the flyout comes from a view implements following i/f
	 *  {
	 *    getRenderedHtml : function() { return html string }
	 *    onPopupShown : function() {}, optional
	 *    onPopupClosed : function() {}, optional
	 *  }
	 *
	 * There is only one flyout visible at a time, clicking/tapping/scrolling/resizing on rest of the
	 * page or on another element associated with a flyout will close the
	 * previous flyout.
	 *
	 * Input options to Flyout
	 * {
	 *  selector : jQuery constructor argument for the DOM node associated with the flyout
	 *  flyoutClass: Css class to be added to the flyout node.
	 *  placement: Placement of the popover [top, left, right, bottom]. Default value is 'top'.
	 *  						If there is no room at this placement, we attempt an opposite placement, see this._positionFlyout().
	 *	alignment: Default is 'start' of the selector, i.e top of the selector for a left or right placement and left of the selector for a top or bottom placement.
	 *							Also supported is 'center' and 'end'.
	 *  viewClass, viewInstance, viewOptions, content: integration points with the content of the flyout
	 * }
	 *
	 * @class Flyout
	 */
	var Flyout = BaseClass.extend({

		init: function(options) {
			Flyout.inherited('init', this, [{ launchPoint: options.launchPoint }]);
			this.$flyoutContainer = $('<div class="dashboard-flyout-container">');
			this.logger = options.logger;
			this.$selector = $(options.selector);
			this.$parent = options.parent ? $(options.parent) : $(document.body);
			this.alignment = options.alignment;
			this.placement = options.placement || 'top';
			this.position = {
				left: 0,
				top: 0
			};
			this.enableTabLooping = true;
			this.flyoutClass = options.flyoutClass;

			var view = options.viewInstance;
			if (!view) {
				view = new options.viewClass({
					popupContainer: this,
					content: options.content,
					viewOptions: options.viewOptions,
					logger: this.logger
				});
				this.isDestroyContentView = true;
			}
			this.view = view;
		},

		getPlacement: function() {
			return this.placement;
		},

		_positionFlyout: function(triggerNodeRect, flyoutRect, parentRect, windowWidth, windowHeight, placement) {
			triggerNodeRect = triggerNodeRect || this.$selector[0].getBoundingClientRect();
			flyoutRect = flyoutRect || this.$flyoutContainer[0].firstChild.getBoundingClientRect();
			parentRect = parentRect || this.$parent[0].getBoundingClientRect();
			windowWidth = windowWidth || $(window).width();
			windowHeight = windowHeight || $(window).height();
			placement = placement || this.placement;

			if(placement === 'top') {
				this.position = {
					left: triggerNodeRect.left,
					top: triggerNodeRect.top - flyoutRect.height
				};
			} else if(placement === 'left') {
				this.position = {
					left: triggerNodeRect.left - flyoutRect.width,
					top: triggerNodeRect.top
				};
			} else if(placement === 'right') {
				this.position = {
					left: triggerNodeRect.left + triggerNodeRect.width,
					top: triggerNodeRect.top
				};
			} else if(placement === 'bottom') {
				this.position = {
					left: triggerNodeRect.left,
					top: triggerNodeRect.top + triggerNodeRect.height
				};
			}
			if(this.alignment === 'center') {
				if(placement === 'top' || placement === 'bottom') {
					this.position.left -= (flyoutRect.width - triggerNodeRect.width) / 2;
				} else if(placement === 'left' || placement === 'right') {
					this.position.top -= (flyoutRect.height - triggerNodeRect.height) / 2;
				}
			}
			if(this.alignment === 'end') {
				if(placement === 'top' || placement === 'bottom') {
					this.position.left += triggerNodeRect.width;
				} else if(placement === 'left' || placement === 'right') {
					this.position.top += triggerNodeRect.height;
				}
			}
			flyoutRect = {
				bottom: this.position.top + flyoutRect.height,
				right: this.position.left + flyoutRect.width,
				top: this.position.top,
				left: this.position.left,
				width: flyoutRect.width,
				height: flyoutRect.height
			};
			const adjusted = this._adjustFlyoutInViewport(flyoutRect, windowWidth, windowHeight, placement);
			this.position = {
				left: this.position.left - parentRect.left + this.$parent[0].offsetLeft,
				top: this.position.top - parentRect.top + this.$parent[0].offsetTop
			};
			if(adjusted && placement === this.placement) {
				// First attempt and we had to move the flyout back on screen, we favor an opposite placement if it fits on screen without adjusting.
				const placementOppositeMap = {
					top: 'bottom',
					right: 'left',
					bottom: 'top',
					left: 'right'
				};
				const adjustedPositionFirstAttempt = JSON.parse(JSON.stringify(this.position));
				const candidatePlacement = placementOppositeMap[placement];
				const adjustedSecondAttempt = this._positionFlyout(triggerNodeRect, flyoutRect, parentRect, windowWidth, windowHeight, candidatePlacement);
				if(adjustedSecondAttempt) {
					this.position = adjustedPositionFirstAttempt;
				} else {
					this.placement = candidatePlacement;
				}
			}
			this.$flyoutContainer.css({
				top: this.position.top,
				left: this.position.left
			});

			return adjusted;
		},

		_close: function() {
			this.isOpen = false;
			this.detachHideEvents();
			ReactDOM.unmountComponentAtNode(this.$flyoutContainer[0]);
			this.$flyoutContainer.remove();
			this.flyoutComponent = null;
			if(this.view.onPopupClosed) {
				this.view.onPopupClosed();
			}
		},

		open: function() {
			if (!this.isOpen) {
				this.$parent[0].appendChild(this.$flyoutContainer[0]);
				this.flyoutComponent = ReactDOM.render(
					<Toolkit.Flyout
						domNodeToAttachTo={this.$flyoutContainer[0]}
						fixedPosition={{ x: 0, y: 0 }}
						triggerNode={this.$selector[0]}
						hideArrow={true}
						onClose={this.close.bind(this)}
						className={this.flyoutClass}
						style={{ position: 'relative' }}
					>
						<div className={'popover-content'}/>
					</Toolkit.Flyout>
					, this.$flyoutContainer[0]);
				const content = this.view.getRenderedHtml();
				this.$flyoutContent = this.$flyoutContainer.find('.popover-content');
				this.$flyoutContent.append(content);
				this._positionFlyout();
				this._bindEvents();
				// In the previous implementation of Flyout (bootstrap), the callback was invoked in the next cycle of execution.
				// Some implementations rely on that (known e.g FilterDialog), so wrapping in setTimeout 0.
				setTimeout(() => {
					if(this.view && this.view.onPopupShown) {
						this.view.onPopupShown();
					}
				}, 0);
				this._setFocus();
			}
			this.isOpen = true;
		},

		_adjustFlyoutInViewport: function(flyoutRect, windowWidth, windowHeight, placement) {
			let adjusted = false;

			if (windowWidth < flyoutRect.right) {
				let distanceToMove = Math.min(flyoutRect.right - windowWidth, flyoutRect.left);
				this.position.left = flyoutRect.left - distanceToMove;
				if(placement === 'right') {
					adjusted = true;
				}
			}
			if (windowHeight < flyoutRect.bottom) {
				let distanceToMove = Math.min(flyoutRect.bottom - windowHeight, flyoutRect.top);
				this.position.top = flyoutRect.top - distanceToMove;
				if(placement === 'bottom') {
					adjusted = true;
				}
			}

			if(this.position.left < 0) {
				this.position.left = 0;
				if(placement === 'left') {
					adjusted = true;
				}
			}
			if(this.position.top < 0) {
				this.position.top = 0;
				if(placement === 'top') {
					adjusted = true;
				}
			}

			return adjusted;
		},

		close: function(){
			if(this.isOpen) {
				this._close();
				this.view.trigger && this.view.trigger('flyout:hide');
				this._restoreFocus();
			}
		},

		_restoreFocus: function() {
			$(this.getLaunchPoint()).focus();
		},

		destroy: function() {
			this._close();
			if(this.isDestroyContentView && this.view.destroy ) {
				this.view.destroy();
				this.view = null;
			}
		},

		detachHideEvents: function() {
			if(this.scrollHideEventHandler) {
				$('body').off('wheel.flyoutHideEvent touchstart.flyoutHideEvent', this.scrollHideEventHandler);
				this.scrollHideEventHandler = null;
			}
			if (this.keyboardHideEventHandler) {
				$('body').off('keydown.flyoutHideEvent', this.keyboardHideEventHandler);
				this.keyboardHideEventHandler = null;
			}
			$(window).off('resize.flyoutResizeListener');
		},

		_bindEvents: function() {
			this.scrollHideEventHandler = (e) => {
				if(!$.contains(this.$flyoutContainer[0], e.target)) {
					this.close();
				}
			};
			// Keyboard hide event handler
			this.keyboardHideEventHandler = (e) => {
				if (e.keyCode === 27 || (e.keyCode === 219 && e.ctrlKey)) {
					//Escape or (Ctrl + [) to close
					this.close();
				}
			};
			$('body').on('wheel.flyoutHideEvent touchstart.flyoutHideEvent', this.scrollHideEventHandler);
			$('body').on('keydown.flyoutHideEvent', this.keyboardHideEventHandler);
			this.windowWidth = window.innerWidth;
			$(window).on('resize.flyoutResizeListener', () => {
				//Only handle the case when window width has actually changed because iPad would send unexpected 
				//resize event whenever there is a scrolling occures;
				if (Utils.isIpad() && this.windowWidth === window.innerWidth) {
					return;
				}
				this.windowWidth = window.innerWidth;
				this.close();
			});
			this.$flyoutContent.on('tap', (e) => {
				e.stopPropagation(); // If we tap on the popover, don't let the event bubble out.
			});
		},

		_setFocus: function() {
			if(this.view && this.view.setFocus) {
				this.view.setFocus();
			}
		}

	});

	return Flyout;
});
